import React, { useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import './WordPressPostModal.css';

interface WordPressPostModalProps {
  wordpressPostId: number;
  onClose: () => void;
}

interface WordPressPost {
  wp_id: number;
  title: string;
  content: string;
  excerpt: string;
  author_name: string;
  published_date: string;
  modified_date: string;
  status: string;
  featured_media_url: string | null;
}

const WordPressPostModal: React.FC<WordPressPostModalProps> = ({
  wordpressPostId,
  onClose,
}) => {
  const [post, setPost] = useState<WordPressPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('wordpress_posts')
          .select('*')
          .eq('wp_id', wordpressPostId)
          .maybeSingle();

        if (error) {
          throw error;
        }

        if (!data) {
          throw new Error('Post not found');
        }

        setPost(data);
      } catch (err) {
        console.error('Error fetching WordPress post:', err);
        setError(err instanceof Error ? err.message : 'Failed to load post');
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [wordpressPostId]);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="wp-post-modal-overlay" onClick={onClose}>
      <div className="wp-post-modal" onClick={(e) => e.stopPropagation()}>
        <div className="wp-post-modal-header">
          <h2>WordPress Post</h2>
          <button className="wp-post-modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="wp-post-modal-content">
          {loading && (
            <div className="wp-post-modal-loading">Loading post...</div>
          )}

          {error && (
            <div className="wp-post-modal-error">
              <strong>Error:</strong> {error}
            </div>
          )}

          {post && !loading && (
            <>
              {post.featured_media_url && (
                <div className="wp-post-featured-image">
                  <img src={post.featured_media_url} alt={post.title} />
                </div>
              )}

              <div className="wp-post-meta">
                <span className="wp-post-author">By {post.author_name}</span>
                <span className="wp-post-date">
                  {formatDate(post.published_date)}
                </span>
                <span className={`wp-post-status status-${post.status}`}>
                  {post.status}
                </span>
              </div>

              <h1 className="wp-post-title">{post.title}</h1>

              <div
                className="wp-post-content"
                dangerouslySetInnerHTML={{ __html: post.content }}
              />

              {post.modified_date !== post.published_date && (
                <div className="wp-post-modified">
                  Last modified: {formatDate(post.modified_date)}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default WordPressPostModal;
